import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { ReactNode, useCallback, useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { ContactInformation } from '@components/profile_form/form'
import { profileSchema, setupInitialValues, TProfileSchema } from '@components/profile_form/schema'
import { useSessionContext } from '@providers/session'
import { useLocationContext } from '@providers/location'
import { buildErrorMessage } from '@shared/error'
import { SMSConsentError } from 'components-ui/src/shared/error'
import { captureException } from 'service-api/src/shared'
import { i18WithParams as t } from '@shared/locale'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { ButtonActions, NavBlocker } from 'components-ui'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { Hooks, Services, Types } from 'service-api'

const ProfileWrapper = ({ children }: { children: ReactNode }) => {
  const { navigateRoute } = useRoutes()

  const btnBackHandler = () => navigateRoute('myprofile', {})

  return (
    <Box sx={{ width: '100%' }}>
      <Button variant="text" onClick={btnBackHandler} startIcon={<KeyboardArrowLeftIcon />}>
        {t('profile.backLink')}
      </Button>
      <Spacer height={2} />
      {children}
    </Box>
  )
}

interface TMyProfileEditInnerProps {
  onFormState: (isDirty: boolean) => void
}

export const MyProfileEditInner = ({ onFormState }: TMyProfileEditInnerProps) => {
  const { navigateRoute } = useRoutes()
  const sessionCtx = useSessionContext()
  const locationCtx = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user!.lupId

  const lup = Hooks.useClientLocationUserProfile(locationName, lupId)
  const [optIn, setOptIn] = useState<boolean>(lup.data?.smsConsentStatus === 'ACTIVE')
  const isSMSOptInDirty = useMemo(() => {
    // if lup value matches toggle value, return not dirty
    if ((optIn && lup.data?.smsConsentStatus === 'ACTIVE') || (!optIn && lup.data?.smsConsentStatus === 'INACTIVE')) {
      return false
    } else {
      return true
    }
  }, [optIn, lup.data?.smsConsentStatus])

  const formik = useFormik<TProfileSchema>({
    enableReinitialize: true,
    initialValues: setupInitialValues(lup.data),
    validationSchema: profileSchema,
    onSubmit: async (values): Promise<boolean> => {
      try {
        if (isSMSOptInDirty) {
          // submit new sms status
          Services.ClientSMSConsentService.updateSMSConsent(locationName, {
            consentType: 'SMS',
            lupId: lupId,
            operatorEmail: values.email,
            phoneNumber: values.phone,
            consentStatus: optIn ? 'ACTIVE' : 'INACTIVE',
          })
            .catch((e) => {
              captureException(new SMSConsentError((e as Error).message), {
                context: {
                  locationUserProfileId: lupId || '',
                },
              })
              enqueueSnackbar(buildErrorMessage(e), { variant: 'error' })
            })
            .then((res) => {
              if (res.success) {
                enqueueSnackbar(t('contactForm.smsConsent.success'), { variant: 'success' })
              }
            })
            .finally(() => lup.refetch())
        }
        if (formik.dirty) {
          await Services.ClientLocationUserProfileService.put(locationName, lupId, {
            displayName: `${values.firstName} ${values.lastName}`,
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone,
            streetAddress: values.streetAddress,
            streetAddress2: (values.streetAddress2?.trim() ?? '') === '' ? null : values.streetAddress2,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
          } as Types.LocationUserProfileCreateInput)
        }
      } catch (error) {
        captureError(error as Error)
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        return false
      }

      // Timeout to let form reset settle and not be catch by NavBlocker
      setTimeout(() => navigateRoute('myprofile', {}), 300)
      onFormState(false)
      enqueueSnackbar(t('profile.update.success'), { variant: 'success' })
      return true
    },
  })

  useEffect(() => onFormState(formik.dirty), [formik.dirty, onFormState])

  if (lup.isLoading) {
    return (
      <ProfileWrapper>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </ProfileWrapper>
    )
  }

  const onSubmitHandler = async () => {
    await formik.submitForm()
  }
  const onCancelHandler = async () => {
    if (formik.dirty) {
      formik.resetForm()
      return
    }

    setTimeout(() => navigateRoute('myprofile', {}))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formSelectHandler = (_ev: React.ChangeEvent<any>, value: any) => {
    formik.setFieldValue('state', value.code)
  }

  return (
    <ProfileWrapper>
      <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Box>
          <Typography variant="h5">{t('profile.edit.title')}</Typography>
          <Spacer height={3} />
          <ContactInformation
            formik={formik}
            onChange={formik.handleChange}
            onChangeSelect={formSelectHandler}
            size="medium"
          />
          <Stack p={1} borderRadius={1} sx={{ backgroundColor: theme.palette.action.hover }} direction={'column'}>
            <Typography variant="h6">{t('contactForm.smsConsent')}</Typography>
            <Typography>
              {t('contactForm.smsConsent.notice', { locationName: locationCtx.location?.displayName || '' })}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={optIn} onClick={() => setOptIn((prev) => !prev)} />}
                label={optIn ? t('contactForm.smsConsent.optOut') : t('contactForm.smsConsent.optIn')}
              />
            </FormGroup>
          </Stack>
          <Spacer height={2} />
          <ButtonActions
            onAction={onSubmitHandler}
            actionEnabled={formik.dirty || isSMSOptInDirty}
            onCancel={onCancelHandler}
            invertButtons
          />
        </Box>
      </Paper>
    </ProfileWrapper>
  )
}

export const MyProfileEdit = () => {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formStateHandler = useCallback((isDirty: boolean) => setIsFormDirty(isDirty), [])

  return (
    <NavBlocker
      sx={{ px: 3, py: 2, width: 444 }}
      shouldBlock={isFormDirty}
      promptTitle={t('profile.navBlocker.title')}
      promptContent={t('profile.navBlocker.content')}
      goBackText={t('profile.navBlocker.back')}
      proceedText={t('profile.navBlocker.proceed')}
    >
      <MyProfileEditInner onFormState={formStateHandler} />
    </NavBlocker>
  )
}
